/**
 * QrScanner — a full-screen camera QR reader for zero-paste connect. Decodes the
 * first QR it sees and hands the raw text to onResult (the caller parses it as a
 * Fume join link). Holds no session state; falls back to manual link entry when the
 * camera is unavailable or permission is denied.
 */
import { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';

export function QrScanner(props: { onResult: (text: string) => void; onCancel: () => void }): JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const doneRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [manual, setManual] = useState('');

  useEffect(() => {
    let stream: MediaStream | null = null;
    let raf = 0;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    const tick = (): void => {
      const video = videoRef.current;
      if (doneRef.current) return;
      if (video && ctx && video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
        if (code && code.data) {
          doneRef.current = true;
          props.onResult(code.data);
          return;
        }
      }
      raf = requestAnimationFrame(tick);
    };

    void (async (): Promise<void> => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play();
        raf = requestAnimationFrame(tick);
      } catch (e) {
        setError(
          (e as Error).name === 'NotAllowedError'
            ? 'Camera permission was denied. Paste the link instead.'
            : 'No camera available here. Paste the link instead.',
        );
      }
    })();

    return () => {
      doneRef.current = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitManual = (e: React.FormEvent): void => {
    e.preventDefault();
    const v = manual.trim();
    if (v) props.onResult(v);
  };

  return (
    <main className="app scanner">
      <header className="appbar">
        <h1>Scan a Fume QR</h1>
        <button type="button" className="mini" onClick={props.onCancel}>
          Cancel
        </button>
      </header>
      {error ? (
        <div className="scanner-fallback">
          <p className="error">{error}</p>
        </div>
      ) : (
        <div className="scanner-view">
          <video ref={videoRef} className="scanner-video" muted playsInline />
          <div className="scanner-frame" aria-hidden="true" />
          <p className="hint">Point the camera at the other phone&rsquo;s Fume QR.</p>
        </div>
      )}
      <form className="codebox" onSubmit={submitManual}>
        <p className="hint">&hellip;or paste the link:</p>
        <textarea
          value={manual}
          rows={2}
          placeholder="https://fumeit.com/#r=…&s=…"
          onChange={(e) => setManual(e.target.value)}
        />
        <button type="submit" disabled={manual.trim().length === 0}>
          Connect from link
        </button>
      </form>
    </main>
  );
}
